/**
 * OBSIDIAN node glyphs as React/SVG components — a faithful port of the
 * signed-off vanilla renderer obgraph.js (entityGlyphCollar, entityGlyphSimple,
 * activityGlyph, boundaryGlyph, geoGlyph, mitreGlyph, labelHTML) and graph-spec.md §1.
 *
 * Shared by the web client and the browser extension so both stay lockstep.
 *
 *   Grammar: fill = category · ring = severity · SHAPE = ontological class ·
 *            TYPE rides the outline · motion only on selection.
 *
 * Icon color: white on dark theme, the category STROKE color on light theme.
 * The entity icon ratio is 0.34× diameter and the alert icon 0.34×s (NOT 0.42).
 */

import type { CSSProperties, ReactElement } from 'react';

// ── inline Lucide icon paths (the real set, from obgraph.js LU) ──────────────
const LU: Record<string, string> = {
  globe:
    '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
  'radio-tower':
    '<path d="M4.9 16.1C1 12.2 1 5.8 4.9 1.9"/><path d="M7.8 4.7a6.14 6.14 0 0 0-.8 7.5"/><circle cx="12" cy="9" r="2"/><path d="M16.2 4.8c2 2 2.26 5.11.8 7.47"/><path d="M19.1 1.9a9.96 9.96 0 0 1 0 14.1"/><path d="M9.5 18h5"/><path d="m8 22 4-11 4 11"/>',
  link:
    '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  'app-window':
    '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="M10 4v4"/><path d="M2 8h20"/><path d="M6 4v4"/>',
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  users:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  monitor:
    '<rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/>',
  'triangle-alert':
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  crosshair:
    '<circle cx="12" cy="12" r="10"/><line x1="22" x2="18" y1="12" y2="12"/><line x1="6" x2="2" y1="12" y2="12"/><line x1="12" x2="12" y1="6" y2="2"/><line x1="12" x2="12" y1="22" y2="18"/>',
  archive:
    '<rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/>',
  'map-pin': '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
  server:
    '<rect width="20" height="8" x="2" y="2" rx="2" ry="2"/><rect width="20" height="8" x="2" y="14" rx="2" ry="2"/><line x1="6" x2="6.01" y1="6" y2="6"/><line x1="6" x2="6.01" y1="18" y2="18"/>',
  database:
    '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/>',
  default: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>',
};

/** iconSvg-equivalent: an inline Lucide SVG via dangerouslySetInnerHTML. */
function Icon({ name, px, color, sw }: { name: string; px: number; color: string; sw: number }): ReactElement {
  const paths = LU[name] ?? LU['default']!;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ stroke: color, display: 'block' }}
      dangerouslySetInnerHTML={{ __html: paths }}
    />
  );
}

// ── shared types ─────────────────────────────────────────────────────────────
export interface GlyphProps {
  /** Lucide icon name (from ontologyGlyph.iconNameFor). */
  icon: string;
  /** category fill color (resolved hex). */
  fill: string;
  /** category stroke color (resolved hex) — used as icon color on light theme. */
  stroke: string;
  /** ring/severity color (resolved hex). */
  ring: string;
  /** ring stroke width (1.25 / 1.75). */
  sw: number;
  dark: boolean;
  /** hub nodes render slightly larger. */
  hub?: boolean;
  /** density scale (1 web, ~0.85 extension compact). */
  scale?: number;
  /** whether this node is glowing (selected / focused). */
  glow?: boolean;
}

function iconColor(stroke: string, dark: boolean): string {
  return dark ? '#ffffff' : stroke;
}

// ── ENTITY — collar circle (TYPE on circumference via <textPath>) ────────────
export function EntityCollarGlyph(props: GlyphProps & { id: string; collarType: string }): ReactElement {
  const { icon, fill, stroke, ring, sw, dark, hub, id, collarType, glow } = props;
  const size = hub ? 52 : 40;
  const fs = 7.5;
  const rr = size / 2 - sw / 2;
  const typeLabel = collarType;
  const txtW = typeLabel.length * fs * 0.62 + 7;
  const circ = 2 * Math.PI * rr;
  const gapHalf = Math.min(74, (txtW / circ) * 180);
  const pad = fs + 7;
  const S = 2 * (size / 2 + pad);
  const c = S / 2;
  const top = -90;
  const a1 = top + gapHalf;
  const a2 = top - gapHalf;
  const pt = (a: number): [number, number] => [
    c + rr * Math.cos((a * Math.PI) / 180),
    c + rr * Math.sin((a * Math.PI) / 180),
  ];
  const [ax0, ay0] = pt(a1);
  const [bx0, by0] = pt(a2);
  const ax = ax0.toFixed(2), ay = ay0.toFixed(2), bx = bx0.toFixed(2), by = by0.toFixed(2);
  const pid = `obgc-${id}`;
  const bodyStyle: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: fill,
    boxShadow: glow
      ? `0 0 0 3px var(--graph-canvas), 0 0 16px ${ring}66`
      : '0 0 0 3px var(--graph-canvas)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'box-shadow 180ms',
  };
  return (
    <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="obg-body" style={bodyStyle}>
        <Icon name={icon} px={Math.round(size * 0.34)} color={iconColor(stroke, dark)} sw={1.85} />
      </div>
      <svg
        width={S}
        height={S}
        viewBox={`0 0 ${S} ${S}`}
        style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', overflow: 'visible' }}
      >
        <defs>
          <path id={pid} d={`M ${bx},${by} A ${rr},${rr} 0 0 1 ${ax},${ay}`} fill="none" />
        </defs>
        <path d={`M ${ax},${ay} A ${rr},${rr} 0 1 1 ${bx},${by}`} fill="none" stroke={ring} strokeWidth={sw} />
        <text
          dominantBaseline="central"
          style={{ fontFamily: 'var(--font-mono)', fontSize: `${fs}px`, fontWeight: 700, letterSpacing: '0.1em', fill: ring }}
        >
          <textPath href={`#${pid}`} startOffset="50%" textAnchor="middle">
            {typeLabel}
          </textPath>
        </text>
      </svg>
    </div>
  );
}

// ── ENTITY — simple LOD variant (ring + icon, no collar) ─────────────────────
export function EntitySimpleGlyph(props: GlyphProps): ReactElement {
  const { icon, fill, stroke, ring, sw, dark, hub, scale = 1, glow } = props;
  const size = (hub ? 46 : 36) * scale;
  return (
    <div
      className="obg-body"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: fill,
        border: `${sw}px solid ${ring}`,
        boxShadow: glow ? `0 0 0 3px var(--graph-canvas), 0 0 14px ${ring}88` : '0 0 0 3px var(--graph-canvas)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'box-shadow 180ms',
      }}
    >
      <Icon name={icon} px={Math.round(size * 0.4)} color={iconColor(stroke, dark)} sw={1.85} />
    </div>
  );
}

// ── ACTIVITY — octagon (alerts) ──────────────────────────────────────────────
export function ActivityOctagonGlyph(props: GlyphProps): ReactElement {
  const { icon, fill, stroke, ring, sw, dark, scale = 1, glow } = props;
  const s = 46 * scale;
  const P: [number, number][] = [
    [0.293, 0], [0.707, 0], [1, 0.293], [1, 0.707],
    [0.707, 1], [0.293, 1], [0, 0.707], [0, 0.293],
  ];
  const pts = P.map((p) => `${(p[0] * (s - 2) + 1).toFixed(1)},${(p[1] * (s - 2) + 1).toFixed(1)}`).join(' ');
  return (
    <div
      className="obg-body"
      style={{ position: 'relative', width: s, height: s, display: 'flex', alignItems: 'center', justifyContent: 'center', filter: glow ? `drop-shadow(0 0 7px ${ring}aa)` : 'none' }}
    >
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <polygon points={pts} fill={fill} stroke={ring} strokeWidth={sw + 0.4} strokeLinejoin="round" />
      </svg>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Icon name={icon || 'triangle-alert'} px={Math.round(s * 0.34)} color={iconColor(stroke, dark)} sw={1.85} />
      </span>
    </div>
  );
}

// ── INTERNET — plain large globe ─────────────────────────────────────────────
export function BoundaryGlobeGlyph({ scale = 1, glow }: { scale?: number; glow?: boolean }): ReactElement {
  const S = 54 * scale;
  return (
    <div
      className="obg-body"
      style={{ position: 'relative', width: S, height: S, display: 'flex', alignItems: 'center', justifyContent: 'center', filter: glow ? 'drop-shadow(0 0 7px var(--color-brand))' : 'none' }}
    >
      <div style={{ position: 'absolute', width: S, height: S, borderRadius: '50%', background: 'var(--graph-canvas)' }} />
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Icon name="globe" px={Math.round(S)} color="var(--color-text-secondary)" sw={1.4} />
      </span>
    </div>
  );
}

// ── LOCATION — muted map pin (property) ──────────────────────────────────────
export function GeoPinGlyph({ color, glow }: { color: string; glow?: boolean }): ReactElement {
  return (
    <div
      className="obg-body"
      style={{ position: 'relative', width: 28, height: 34, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', filter: glow ? `drop-shadow(0 0 5px ${color})` : 'none' }}
    >
      <svg width={28} height={34} viewBox="0 0 28 34" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <path d="M14 1 C6 1 1 6 1 13 C1 22 14 33 14 33 C14 33 27 22 27 13 C27 6 22 1 14 1 Z" fill="var(--color-surface)" stroke={color} strokeWidth={1.5} />
        <circle cx={14} cy={13} r={4.2} fill={color} />
      </svg>
    </div>
  );
}

// ── TECHNIQUE — muted hexagon badge (property) ───────────────────────────────
export function MitreHexGlyph({ glow }: { glow?: boolean }): ReactElement {
  const w = 30, h = 33;
  const pts = `${w / 2},1 ${w - 1},${h * 0.27} ${w - 1},${h * 0.73} ${w / 2},${h - 1} 1,${h * 0.73} 1,${h * 0.27}`;
  return (
    <div
      className="obg-body"
      style={{ position: 'relative', width: w, height: h, display: 'flex', alignItems: 'center', justifyContent: 'center', filter: glow ? 'drop-shadow(0 0 5px var(--color-brand))' : 'none' }}
    >
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <polygon points={pts} fill="var(--color-surface)" stroke="var(--color-text-secondary)" strokeWidth={1.5} strokeLinejoin="round" />
      </svg>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Icon name="crosshair" px={15} color="var(--color-text-secondary)" sw={2} />
      </span>
    </div>
  );
}

// ── Node label (below the glyph) ─────────────────────────────────────────────
export interface NodeLabelProps {
  klass: 'entity' | 'activity' | 'boundary' | 'geo' | 'mitre';
  label: string;
  sub?: string;
  ring: string;
  hub?: boolean;
  scale?: number;
  /** show the sub line (only while hovered / selected). */
  showSub?: boolean;
}

export function NodeLabel({ klass, label, sub, ring, hub, scale = 1, showSub }: NodeLabelProps): ReactElement {
  const mono: CSSProperties = { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
  if (klass === 'entity' || klass === 'boundary') {
    const fs = (hub ? 10.5 : 9.5) * scale;
    return (
      <div style={{ textAlign: 'center', lineHeight: 1.15 }}>
        <div style={{ ...mono, fontSize: `${fs}px`, fontWeight: 700, color: 'var(--color-text-primary)', letterSpacing: '0.02em' }}>{label}</div>
        {sub ? (
          <div className="obg-sub" style={{ ...mono, fontSize: '8px', color: 'var(--color-text-muted)', marginTop: 1, display: showSub ? 'block' : 'none' }}>{sub}</div>
        ) : null}
      </div>
    );
  }
  if (klass === 'activity') {
    return (
      <div style={{ textAlign: 'center', lineHeight: 1.15 }}>
        <div style={{ ...mono, fontSize: '7px', fontWeight: 700, letterSpacing: '0.2em', color: ring }}>ALERT</div>
        <div style={{ ...mono, fontSize: `${9.5 * scale}px`, fontWeight: 700, color: 'var(--color-text-primary)', letterSpacing: '0.02em' }}>{label}</div>
        {sub ? <div style={{ ...mono, fontSize: '8px', color: 'var(--color-text-muted)', display: showSub ? 'block' : 'none' }}>{sub}</div> : null}
      </div>
    );
  }
  const eyebrow = klass === 'geo' ? 'LOCATION' : 'TECHNIQUE';
  return (
    <div style={{ textAlign: 'center', lineHeight: 1.1 }}>
      <div style={{ ...mono, fontSize: '9px', fontWeight: 700, color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ ...mono, fontSize: '6.5px', fontWeight: 700, letterSpacing: '0.18em', color: 'var(--color-text-muted)' }}>{eyebrow}</div>
    </div>
  );
}
